import { FC, useEffect, useMemo, useState } from "react";
import { IconBuildingWarehouse } from "@tabler/icons-react";
import { ScrollArea, Stack } from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";

import NavItem from "./NavItem";
import { getNavbarItemsByPermissions, NavMenuItem } from "./navbarConfig";
import classes from "./Navbar.module.css";
import { useAbility } from "configs/casl";

type Props = {
  triggerBurger: () => void;
  isExpanded?: boolean;
};

const NavbarCustom: FC<Props> = ({ triggerBurger, isExpanded = false }) => {
  const ability = useAbility();
  const navItems = useMemo(
    () => getNavbarItemsByPermissions(ability),
    [ability],
  );
  const location = useLocation();
  const navigate = useNavigate();

  const [expandedItem, setExpandedItem] = useState<NavMenuItem | null>(null);

  // Reset accordion when toggling mode
  useEffect(() => {
    setExpandedItem(null);
  }, [isExpanded]);

  // Auto-sync accordion with current route in expanded mode
  useEffect(() => {
    if (!isExpanded) return;
    const activeParent = navItems.find((item) =>
      item.children.some((c) => c.link === location.pathname)
    );
    setExpandedItem(activeParent ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isExpanded]);

  const handleSelect = (item: NavMenuItem) => {
    if (isExpanded && item.children.length > 0) {
      setExpandedItem(expandedItem?.key === item.key ? null : item);
    } else if (item.children.length === 0) {
      setExpandedItem(null);
      triggerBurger();
      navigate(item.link);
    }
  };

  const handleChildNavigate = (link: string) => {
    triggerBurger();
    navigate(link);
  };

  return (
    <nav className={classes.navbar} data-expanded={isExpanded || undefined}>
      <div className={classes.logoArea}>
        <div className={classes.linkIconBox}>
          <IconBuildingWarehouse size={22} stroke={1.7} color="white" />
        </div>
        <span className={`${classes.logoTitle}${!isExpanded ? ` ${classes.linkLabelHidden}` : ""}`}>
          Quản lý kho
        </span>
      </div>

      <ScrollArea
        className={classes.navbarMain}
        scrollbarSize={4}
        styles={{ viewport: { padding: 0 } }}
      >
        <Stack gap={isExpanded ? 2 : 4}>
          {navItems.map(({ key, ...item }) => {
            const isActive = !!item.link && location.pathname === item.link;
            const isChildActive = item.children.some((c) => c.link === location.pathname);

            return (
              <NavItem
                key={key}
                icon={item.icon}
                label={item.label}
                link={item.link}
                children={item.children}
                isNavExpanded={isExpanded}
                isAccordionOpen={expandedItem?.key === key}
                showActive={isActive || isChildActive}
                currentPath={location.pathname}
                onSelect={() => handleSelect({ key, ...item })}
                onChildNavigate={handleChildNavigate}
              />
            );
          })}
        </Stack>
      </ScrollArea>
    </nav>
  );
};

export default NavbarCustom;
